import React from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { Sparkles } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { ChatMessage as ChatMessageType } from '../../../../../types/case-workspace';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { parseDecisionReferences } from '../../../../../utils/decisionLinks';

interface ChatMessageProps {
  message: ChatMessageType;
  onDecisionClick?: (decisionIdOrReference: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onDecisionClick }) => {
  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Process content to make DRN references bold
  // Simple approach: just make DRNs bold for now
  const processContentWithDRNLinks = (content: string): string => {
    // Remove any existing markdown links with decision-modal: protocol (clean them up)
    let processed = content.replace(
      /\[(DRN-\d+)\]\(decision-modal:(DRN-\d+)\)/gi,
      (match, drn1) => drn1
    );
    
    // Also clean up any other markdown links containing DRNs
    processed = processed.replace(
      /\[(DRN-\d+)\]\([^)]+\)/gi,
      (match, drn) => drn
    );
    
    // Make plain DRN references bold
    const DRN_PATTERN = /\bDRN-\d{1,10}\b/gi;
    return processed.replace(DRN_PATTERN, (match) => `**${match}**`);
  };

  return (
    <div
      className={cn(
        "flex gap-3",
        message.role === 'user' ? "justify-end" : "justify-start"
      )}
    >
      {/* AI Avatar */}
      {message.role === 'assistant' && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
      )}

      {/* Message Bubble */}
      <div
        className={cn(
          "max-w-[80%] rounded-lg p-3 text-sm",
          message.role === 'user'
            ? "bg-primary text-white"
            : "bg-gray-100 text-foreground"
        )}
      >
        {message.role === 'assistant' ? (
          <div className="prose prose-sm max-w-none prose-p:leading-relaxed prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:mt-3 prose-headings:mb-2 prose-strong:font-semibold prose-strong:text-foreground prose-code:bg-gray-200 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Default link rendering for regular markdown links
                a: ({ node, href, children, ...props }) => (
                  <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
              }}
            >
              {processContentWithDRNLinks(message.content)}
            </ReactMarkdown>
          </div>
        ) : (
          <p className="leading-relaxed whitespace-pre-wrap">{message.content}</p>
        )}

        {/* Timestamp */}
        <span
          className={cn(
            "text-xs mt-1 block",
            message.role === 'user' ? "opacity-70" : "text-muted-foreground"
          )}
        >
          {formatTime(message.timestamp)}
        </span>
      </div>

      {/* User Avatar */}
      {message.role === 'user' && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
          <UserIcon className="w-4 h-4 text-gray-600" />
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
